import { useTheme } from '@doxyme/ui-foundation/theme';
import { type CSSObject } from '@emotion/react';
import { type VoiceIndicatorProps } from '../voiceIndicator';

type VideoIndicatorStyles = {
  disabled: boolean;
} & Record<'container' | 'icon' | 'status', CSSObject>;

type VideoIndicatorStylesProps = {
  hasVideo: boolean;
  hasProblem: boolean;
  isNonInteractive: boolean;
  isLoading: boolean;
  size: VoiceIndicatorProps['size'];
  onClick: VoiceIndicatorProps['onClick'];
};

export const useVideoIndicatorStyles = ({
  hasVideo,
  hasProblem,
  isNonInteractive,
  isLoading,
  size,
  onClick,
}: VideoIndicatorStylesProps): VideoIndicatorStyles => {
  const theme = useTheme();

  const disabled = hasProblem || isNonInteractive;

  return {
    disabled,
    container: {
      all: 'unset',
      position: 'relative',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexShrink: 0,
      borderRadius:
        size === 'small' ? theme.shape.radius4 : theme.shape.radius8,
      backgroundColor:
        (hasVideo && !hasProblem && !disabled) || isLoading
          ? theme.iconButton.background.defaultAlpha.default
          : theme.palette.common.white,
      boxShadow: theme.shadows[2],
      padding:
        size === 'large'
          ? theme.spacing(2)
          : size === 'medium'
            ? '6px'
            : theme.spacing(1),
      cursor: onClick ? 'pointer' : 'auto',

      ...(onClick && {
        '&:hover': {
          backgroundColor:
            (hasVideo && !hasProblem && !disabled) || isLoading
              ? theme.iconButton.background.defaultAlpha.hover
              : theme.background.interactive.canvasHover,
        },

        '&:focus:not(:focus-visible)': {
          outline: 'none',
        },
        '&:focus-visible': {
          outline: `2.5px solid ${theme.border.interactive.indicatorFocused}`,
          outlineOffset: 4,
        },
      }),

      '&:disabled': {
        cursor: 'not-allowed',
      },
    },
    icon: {
      color: disabled
        ? theme.icon.interactive.disabled
        : hasVideo
          ? theme.icon.onBrand
          : theme.icon.negative,
    },
    status: {
      position: 'absolute',
      right: 0,
      top: 0,
    },
  };
};
